import React, {useContext} from 'react';
import {ButtonPrimary, ButtonLink} from '@homeaway/react-buttons';
import {useIntl} from 'react-intl';
import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';

import {Props as FilterGroupPillProps} from '../FilterGroupPill/FilterGroupPill';
import {FilterGroupType} from '../MoreFilters/MoreFilters';
import {getGroupFiltersSelected} from '../../utils/filters';
import {
    getSelectionByGroup,
    shouldShowClearButton,
} from '../../utils/filterGroupFooterUtils';
import {
    PRICE_FILTER_GROUP_ID,
    ROOMSANDSPACES_FILTER_GROUP_ID,
} from '../../constants/filters';
import {
    MIN_RESULTS_TO_SEARCH,
    CLEAR_PRICE_FILTERS,
    CLEAR_ROOMS_SPACES_FILTERS,
} from '../../utils/constants';

import {getFormattedResults} from '../../utils/localization';
import {findInGroupFilters} from '../../utils/filterPillUtils';
import {
    FILTER_GROUP_CLEAR_SELECTED,
    FILTER_GROUP_MORE_FILTERS_CLEAR_SELECTED,
} from '../../utils/edap';

import './FilterGroupFooter.less';

type Props = Pick<
    FilterGroupPillProps,
    | 'onClear'
    | 'filters'
    | 'selectedFilters'
    | 'results'
    | 'customFiltersParams'
> & {
    groupId?: string;
    filterGroups?: FilterGroupType[];
    onDone: (action?: string) => void;
};

export default function FilterGroupFooter({
    groupId,
    filterGroups,
    onDone,
    onClear,
    results,
    filters,
    selectedFilters,
    customFiltersParams,
}: Props) {
    const intl = useIntl();
    const edapContext: TrackingContextValue = useContext(TrackingContext);

    const selectionByGroup = getSelectionByGroup({
        filters,
        selectedFilters,
        customFiltersParams,
    });

    const showClearButton = shouldShowClearButton({
        groupId,
        filterGroups,
        selectionByGroup,
    });

    // eslint-disable-next-line complexity
    function onClearClick(): void {
        let filtersToClear: Array<string> = [];

        if (groupId) {
            if (groupId === PRICE_FILTER_GROUP_ID) {
                filtersToClear = CLEAR_PRICE_FILTERS;
            } else if (groupId === ROOMSANDSPACES_FILTER_GROUP_ID) {
                filtersToClear = getGroupFiltersSelected(
                    filters,
                    selectedFilters
                )
                    .map(({filter}) => filter.refineByQueryArgument)
                    .concat(CLEAR_ROOMS_SPACES_FILTERS);
            } else {
                filtersToClear = getGroupFiltersSelected(
                    filters,
                    selectedFilters
                ).map(({filter}) => filter.refineByQueryArgument);
            }

            // Trigger the appropriate EDAP event
            edapContext.trigger(FILTER_GROUP_CLEAR_SELECTED, {
                filtergroupid: groupId,
            });
        } else {
            filtersToClear = getGroupFiltersSelected(
                filters,
                selectedFilters
            ).map(({filter}) => filter.refineByQueryArgument);

            /* istanbul ignore else */
            if (filterGroups) {
                /* istanbul ignore else */
                if (
                    findInGroupFilters(filterGroups, PRICE_FILTER_GROUP_ID) &&
                    selectionByGroup.isPriceSelected
                ) {
                    filtersToClear = filtersToClear.concat(CLEAR_PRICE_FILTERS);
                }

                /* istanbul ignore else */
                if (
                    findInGroupFilters(
                        filterGroups,
                        ROOMSANDSPACES_FILTER_GROUP_ID
                    ) &&
                    selectionByGroup.isBedroomOrBathroomSelected
                ) {
                    filtersToClear = filtersToClear.concat(
                        CLEAR_ROOMS_SPACES_FILTERS
                    );
                }

                // Trigger the appropriate EDAP event
                edapContext.trigger(FILTER_GROUP_MORE_FILTERS_CLEAR_SELECTED, {
                    filtergroupid: filterGroups
                        .map((filterGroup) => filterGroup.groupInfo.id)
                        .join(','),
                });
            }
        }

        onClear(filtersToClear);
    }

    function handleOnClick() {
        onDone('applied');
    }

    return (
        <div className="FilterGroupFooter">
            <span className="FilterGroupFooter__Results">
                {results === null
                    ? ''
                    : getFormattedResults(results, intl.formatMessage)}
            </span>
            <span>
                {showClearButton && (
                    <ButtonLink
                        className="FilterGroupFooter__Button FilterGroupFooter__Button--clear"
                        id="filter-group-clear"
                        label="Clear"
                        size="sm"
                        onClick={onClearClick}
                    />
                )}
                <ButtonPrimary
                    className="FilterGroupFooter__Button"
                    id="filter-group-done"
                    label={intl.formatMessage({
                        id: 'done',
                        defaultMessage: 'Done',
                    })}
                    size="sm"
                    onClick={handleOnClick}
                    disabled={results < MIN_RESULTS_TO_SEARCH}
                />
            </span>
        </div>
    );
}
